import React, { useEffect, useState } from 'react';
import apinode from '../../services/apinode';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import ReactPaginate from 'react-paginate';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShoppingCart, faSearch, faCalendar, faChartLine, faFilter } from '@fortawesome/free-solid-svg-icons';
import Navbar from '../../components/navbar/navbar';
import './PricesTable.css'; // Importar o CSS customizado

dayjs.extend(isBetween);

const PricesTable = ({ conta }) => {
    const [data, setData] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sellers, setSellers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [priceDifference, setPriceDifference] = useState('none');
    const [currentPage, setCurrentPage] = useState(0);
    const itemsPerPage = 10;

    useEffect(() => {
        const fetchPrices = async () => {
            try {
                const response = await apinode.get(`/exportml/listar-precos/${localStorage.idconta}`);
                setData(response.data);

                const uniqueSellers = new Set();
                Object.values(response.data).forEach(item => {
                    Object.keys(item.prices).forEach(sellerId => {
                        uniqueSellers.add(sellerId);
                    });
                });
                setSellers(Array.from(uniqueSellers));
            } catch (err) {
                setError(err.response?.data?.message || err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchPrices();
    }, [conta]);

    if (loading) {
        return (
            <div className="loading-container">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Carregando...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="error-container">
                <div className="alert alert-danger" role="alert">
                    <i className="fas fa-exclamation-circle me-2"></i>
                    {error}
                </div>
            </div>
        );
    }

    const getPriceTags = (prices) => {
        const priceValues = Object.values(prices).map(item => item.price);
        const uniquePrices = new Set(priceValues);

        if (uniquePrices.size === 1) {
            return { allEqual: 'Preços Iguais' };
        }

        const minPrice = Math.min(...priceValues);
        const maxPrice = Math.max(...priceValues);
        const tags = {};

        for (const [sellerId, { price }] of Object.entries(prices)) {
            if (price === minPrice && priceValues.length > 1) {
                tags[sellerId] = 'Menor Preço';
            }
            if (price === maxPrice && priceValues.length > 1) {
                tags[sellerId] = 'Maior Preço';
            }
        }

        return tags;
    };

    const calculatePriceDifference = (prices) => {
        const priceValues = Object.values(prices).map(item => item.price);
        return Math.max(...priceValues) - Math.min(...priceValues);
    };

    const filteredData = Object.entries(data)
        .filter(([sku, { title, prices }]) => {
            const searchMatch = sku.includes(searchTerm) || title.toLowerCase().includes(searchTerm.toLowerCase());

            const dateMatch = Object.values(prices).some(price => {
                const lastUpdate = dayjs(price.last_update);
                const start = startDate ? dayjs(startDate) : null;
                const end = endDate ? dayjs(endDate) : null;

                if (start && end) {
                    return lastUpdate.isBetween(start, end, 'day', '[]');
                } else if (start) {
                    return lastUpdate.isAfter(start.subtract(1, 'day'));
                } else if (end) {
                    return lastUpdate.isBefore(end.add(1, 'day'));
                }
                return true;
            });

            const priceDiff = calculatePriceDifference(prices);
            const priceDiffMatch = (priceDifference === 'none') ||
                (priceDifference === 'low' && priceDiff <= 10) ||
                (priceDifference === 'medium' && priceDiff > 10 && priceDiff <= 50) ||
                (priceDifference === 'high' && priceDiff > 50);

            return searchMatch && dateMatch && priceDiffMatch;
        })
        .sort((a, b) => {
            const lastUpdateA = Math.max(...Object.values(a[1].prices).map(price => new Date(price.last_update)));
            const lastUpdateB = Math.max(...Object.values(b[1].prices).map(price => new Date(price.last_update)));
            return lastUpdateB - lastUpdateA;
        });

    const indexOfLastItem = (currentPage + 1) * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);

    const handlePageChange = ({ selected }) => {
        setCurrentPage(selected);
    };

    return (
        <>
            <Navbar />
          
            <div className="prices-container">
                <div className="prices-header">
                    <h1 className="prices-title">
                        <FontAwesomeIcon icon={faChartLine} className="me-3" />
                        Monitoramento de Preços
                    </h1>
                    <p className="prices-subtitle">
                        Acompanhe e compare os preços dos seus produtos em tempo real
                    </p>
                </div>

                {/* Card de Filtros */}
                <div className="filters-card">
                    <div className="filters-header">
                        <FontAwesomeIcon icon={faFilter} className="me-2" />
                        <span>Filtros</span>
                    </div>
                    <div className="filters-body">
                        <div className="filter-group">
                            <label className="filter-label">
                                <FontAwesomeIcon icon={faSearch} className="me-2" />
                                Buscar
                            </label>
                            <input
                                type="text"
                                className="form-control filter-input"
                                placeholder="SKU ou descrição do produto..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <div className="filter-group">
                            <label className="filter-label">
                                <FontAwesomeIcon icon={faCalendar} className="me-2" />
                                Data Início
                            </label>
                            <input
                                type="date"
                                className="form-control filter-input"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                        </div>

                        <div className="filter-group">
                            <label className="filter-label">
                                <FontAwesomeIcon icon={faCalendar} className="me-2" />
                                Data Fim
                            </label>
                            <input
                                type="date"
                                className="form-control filter-input"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                        </div>

                        <div className="filter-group">
                            <label className="filter-label">
                                <FontAwesomeIcon icon={faChartLine} className="me-2" />
                                Divergência
                            </label>
                            <select
                                className="form-select filter-input"
                                value={priceDifference}
                                onChange={(e) => setPriceDifference(e.target.value)}
                            >
                                <option value="none">Todas</option>
                                <option value="low">Baixa (≤ R$ 10)</option>
                                <option value="medium">Média (R$ 10 - R$ 50)</option>
                                <option value="high">Alta (> R$ 50)</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Informações de resultados */}
                <div className="results-info">
                    <span className="badge bg-primary">
                        {filteredData.length} produtos encontrados
                    </span>
                </div>

                {/* Tabela */}
                <div className="table-card">
                    <div className="table-responsive">
                        <table className="table prices-table">
                            <thead>
                                <tr>
                                    <th className="sku-column">SKU</th>
                                    <th className="title-column">Produto</th>
                                    {sellers.map(sellerId => (
                                        <th key={sellerId} className="seller-column">{sellerId}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {currentItems.map(([sku, { title, prices }]) => {
                                    const priceTags = getPriceTags(prices);
                                    const isEqualPrices = priceTags.allEqual ? priceTags.allEqual : null;

                                    return (
                                        <tr key={sku} className="product-row">
                                            <td className="sku-cell">{sku}</td>
                                            <td className="title-cell">{title}</td>
                                            {sellers.map(sellerId => (
                                                <td key={sellerId} className="price-cell">
                                                    {prices[sellerId] ? (
                                                        <div className="price-info">
                                                            <div className="price-value">
                                                                R$ {prices[sellerId].price.toFixed(2)}
                                                            </div>
                                                            <div className="price-date">
                                                                {dayjs(prices[sellerId].last_update).format('DD/MM/YYYY HH:mm')}
                                                            </div>
                                                            <div className="price-badges">
                                                                {prices[sellerId].sold > 0 && (
                                                                    <span className="badge badge-sold">
                                                                        <FontAwesomeIcon icon={faShoppingCart} className="me-1" />
                                                                        {prices[sellerId].sold.toFixed(2)}
                                                                    </span>
                                                                )}
                                                                {isEqualPrices && (
                                                                    <span className="badge badge-equal">{isEqualPrices}</span>
                                                                )}
                                                                {priceTags[sellerId] && !isEqualPrices && (
                                                                    <span className={`badge ${priceTags[sellerId] === 'Menor Preço' ? 'badge-lowest' : 'badge-highest'}`}>
                                                                        {priceTags[sellerId]}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <span className="text-muted">N/A</span>
                                                    )}
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Paginação */}
                {totalPages > 1 && (
                    <div className="pagination-container">
                        <ReactPaginate
                            previousLabel={'← Anterior'}
                            nextLabel={'Próximo →'}
                            breakLabel={'...'}
                            pageCount={totalPages}
                            marginPagesDisplayed={2}
                            pageRangeDisplayed={5}
                            onPageChange={handlePageChange}
                            containerClassName={'pagination-custom'}
                            pageClassName={'page-item-custom'}
                            pageLinkClassName={'page-link-custom'}
                            previousClassName={'page-item-custom'}
                            previousLinkClassName={'page-link-custom'}
                            nextClassName={'page-item-custom'}
                            nextLinkClassName={'page-link-custom'}
                            breakClassName={'page-item-custom'}
                            breakLinkClassName={'page-link-custom'}
                            activeClassName={'active-custom'}
                            disabledClassName={'disabled-custom'}
                        />
                    </div>
                )}
            </div>
        </>
    );
};

export default PricesTable;